import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Spinner, Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import InputBox from '../common/InputBox';
import SearchSelect from '../common/SearchSelect';
import CustomDatePicker from '../common/CustomDatePicker';
import {
  listPendingInvoices,
  bulkUpdateIod,
  updateIodByLoad,
  listPendingByLoad,
  processExcelUpload,
  getUploadErrors,
} from '../../services/iod/iod-service';
import styles from './iod.module.css';

const MODE_INVOICE = 'invoice';
const MODE_LOAD = 'load';
const TAB_GRID = 'grid';
const TAB_UPLOAD = 'upload';

const EMPTY_FILTERS = { division: '', company: '', fromDate: '', toDate: '' };

const StatusBadge = ({ status }) => {
  const cls = status === 'PENDING' ? styles['badge-pending']
    : status === 'UPDATED' ? styles['badge-updated']
    : styles['badge-system'];
  return <span className={cls}>{status}</span>;
};

const IodUpdate = () => {
  const [mode, setMode] = useState(MODE_INVOICE);
  const [tab, setTab] = useState(TAB_GRID);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loadNumber, setLoadNumber] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // IOD form
  const [iodDate, setIodDate] = useState('');
  const [iodTime, setIodTime] = useState('');

  // Upload
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Detail modal
  const [detailRow, setDetailRow] = useState(null);

  const stats = {
    total: invoices.length,
    pending: invoices.filter((r) => r.status === 'PENDING').length,
    updated: invoices.filter((r) => r.status === 'UPDATED').length,
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setSelected([]);
    setSearched(false);
    try {
      let res;
      if (mode === MODE_LOAD) {
        if (!loadNumber.trim()) { toast.warn('Enter a load number'); return; }
        res = await listPendingByLoad(loadNumber.trim(), filters);
      } else {
        res = await listPendingInvoices(filters);
      }
      setInvoices(res.data || []);
      setSearched(true);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setLoadNumber('');
    setInvoices([]);
    setSelected([]);
    setSearched(false);
    setIodDate('');
    setIodTime('');
  };

  const toggleSelect = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleAll = () => {
    const pending = invoices.filter((r) => r.status === 'PENDING');
    const allKeys = pending.map((r) => `${r.company}|${r.transactionType}|${r.invoiceNo}`);
    setSelected(selected.length === allKeys.length ? [] : allKeys);
  };

  const handleBulkUpdate = async () => {
    if (!iodDate) { toast.warn('Select IOD date'); return; }
    if (selected.length === 0) { toast.warn('Select at least one invoice'); return; }

    const records = selected.map((key) => {
      const [company, transactionType, invoiceNo] = key.split('|');
      return { company, transactionType, invoiceNo, iodDate, iodTime: iodTime || '00:00' };
    });

    setLoading(true);
    try {
      if (mode === MODE_LOAD) {
        await updateIodByLoad({ loadNumber, iodDate, iodTime: iodTime || '00:00', ...filters });
        toast.success('Load updated successfully');
      } else {
        const res = await bulkUpdateIod(records);
        const { processed = 0, failed = 0 } = res.data || {};
        if (failed > 0) {
          toast.warn(`${processed} updated, ${failed} failed`);
        } else {
          toast.success(`${processed} invoice(s) updated`);
        }
      }
      setSelected([]);
      setIodDate('');
      setIodTime('');
      await handleSearch();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0] || null);
    setUploadResult(null);
  };

  const handleUpload = useCallback(async () => {
    if (!uploadFile) { toast.warn('Select an Excel file'); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const data = await uploadFile.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

      const records = rows.map((r) => ({
        company: String(r['Company'] || r['company'] || '').trim(),
        transactionType: String(r['Transaction Type'] || r['transactionType'] || '').trim(),
        invoiceNo: String(r['Invoice No'] || r['invoiceNo'] || '').trim(),
        iodDate: String(r['IOD Date'] || r['iodDate'] || '').trim(),
        iodTime: String(r['IOD Time'] || r['iodTime'] || '00:00').trim(),
      }));

      const res = await processExcelUpload(records);
      const { fileId, processed, failed } = res.data || {};
      setUploadResult({ fileId, processed, failed });

      if (failed > 0) {
        toast.warn(`${processed} processed, ${failed} failed. See error details below.`);
        const errRes = await getUploadErrors(fileId);
        setUploadResult((prev) => ({ ...prev, errors: errRes.data || [] }));
      } else {
        toast.success(`${processed} record(s) uploaded successfully`);
      }
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [uploadFile]);

  const pendingSelected = invoices.filter(
    (r) => r.status === 'PENDING' && selected.includes(`${r.company}|${r.transactionType}|${r.invoiceNo}`)
  );

  return (
    <>
      {loading && (
        <div className="spinner-box">
          <Spinner animation="border" className="spinner" />
        </div>
      )}

      <Container bsPrefix="main-body">
        <div className="page-title">IOD Update</div>
        <div className="page-sub-title">Update Invoice on Delivery date and time</div>

        {/* Mode tabs */}
        <div className={styles['mode-tabs']}>
          <button
            className={`${styles['mode-tab']} ${mode === MODE_INVOICE ? styles.active : ''}`}
            onClick={() => { setMode(MODE_INVOICE); handleReset(); }}
          >
            By Invoice
          </button>
          <button
            className={`${styles['mode-tab']} ${mode === MODE_LOAD ? styles.active : ''}`}
            onClick={() => { setMode(MODE_LOAD); handleReset(); }}
          >
            By Load Number
          </button>
        </div>

        {/* Grid / Upload tabs */}
        <div className={styles['mode-tabs']}>
          <button
            className={`${styles['mode-tab']} ${tab === TAB_GRID ? styles.active : ''}`}
            onClick={() => setTab(TAB_GRID)}
          >
            Invoice Grid
          </button>
          <button
            className={`${styles['mode-tab']} ${tab === TAB_UPLOAD ? styles.active : ''}`}
            onClick={() => setTab(TAB_UPLOAD)}
          >
            Excel Upload
          </button>
        </div>

        {tab === TAB_GRID && (
          <>
            {/* Filter bar */}
            <div className={styles['filter-bar']}>
              <Row className="g-2 align-items-end">
                {mode === MODE_LOAD && (
                  <Col md={2}>
                    <InputBox
                      type="text"
                      label="Load Number"
                      name="loadNumber"
                      value={loadNumber}
                      onChange={(e) => setLoadNumber(e.target.value)}
                    />
                  </Col>
                )}
                <Col md={2}>
                  <InputBox type="text" label="Division" name="division" value={filters.division} onChange={handleFilterChange} />
                </Col>
                <Col md={2}>
                  <InputBox type="text" label="Company" name="company" value={filters.company} onChange={handleFilterChange} />
                </Col>
                <Col md={2}>
                  <CustomDatePicker label="From Date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} />
                </Col>
                <Col md={2}>
                  <CustomDatePicker label="To Date" name="toDate" value={filters.toDate} onChange={handleFilterChange} />
                </Col>
                <Col md="auto">
                  <div className={styles['filter-actions']}>
                    <button className={`btn ${styles['btn-iod-primary']}`} onClick={handleSearch}>Search</button>
                    <button className={`btn ${styles['btn-iod-outline']}`} onClick={handleReset}>Reset</button>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Stats */}
            {searched && (
              <div className={styles['stats-strip']}>
                <div className={styles['stat-card']}>
                  <div className={styles['stat-value']}>{stats.total}</div>
                  <div className={styles['stat-label']}>Total</div>
                </div>
                <div className={styles['stat-card']}>
                  <div className={styles['stat-value']}>{stats.pending}</div>
                  <div className={styles['stat-label']}>Pending</div>
                </div>
                <div className={styles['stat-card']}>
                  <div className={styles['stat-value']}>{stats.updated}</div>
                  <div className={styles['stat-label']}>Updated</div>
                </div>
              </div>
            )}

            {/* IOD form panel */}
            {selected.length > 0 && (
              <div className={styles['iod-form-panel']}>
                <div className={styles['panel-title']}>
                  Set IOD for {selected.length} selected invoice(s)
                </div>
                <Row className="g-2 align-items-end">
                  <Col md={3}>
                    <CustomDatePicker label="IOD Date *" name="iodDate" value={iodDate} onChange={(e) => setIodDate(e.target.value)} />
                  </Col>
                  <Col md={2}>
                    <InputBox type="time" label="IOD Time" name="iodTime" value={iodTime} onChange={(e) => setIodTime(e.target.value)} />
                  </Col>
                  <Col md="auto">
                    <button className={`btn ${styles['btn-iod-primary']}`} onClick={handleBulkUpdate} disabled={loading}>
                      Update IOD
                    </button>
                  </Col>
                </Row>
              </div>
            )}

            {/* Selection bar */}
            {selected.length > 0 && (
              <div className={styles['selection-bar']}>
                <span>{selected.length} invoice(s) selected</span>
                <button className="btn btn-link" onClick={() => setSelected([])}>Clear selection</button>
              </div>
            )}

            {/* Table */}
            {searched && (
              <div className={styles['iod-table-wrap']}>
                <table className={`table ${styles['iod-table']}`}>
                  <thead>
                    <tr>
                      <th>
                        <Form.Check
                          checked={selected.length > 0 && selected.length === invoices.filter((r) => r.status === 'PENDING').length}
                          onChange={toggleAll}
                        />
                      </th>
                      <th>#</th>
                      <th>Invoice No</th>
                      <th>Company</th>
                      <th>Txn Type</th>
                      <th>Division</th>
                      <th>Transporter</th>
                      <th>Load No</th>
                      <th>Invoice Date</th>
                      <th>IOD Date</th>
                      <th>IOD Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 ? (
                      <tr><td colSpan={12} className="text-center text-muted py-4">No records found</td></tr>
                    ) : (
                      invoices.map((row, idx) => {
                        const key = `${row.company}|${row.transactionType}|${row.invoiceNo}`;
                        const isSelected = selected.includes(key);
                        return (
                          <tr
                            key={key}
                            className={isSelected ? styles['selected-row'] : ''}
                            onClick={() => row.status === 'PENDING' && toggleSelect(key)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              {row.status === 'PENDING' && (
                                <Form.Check checked={isSelected} onChange={() => toggleSelect(key)} />
                              )}
                            </td>
                            <td>{idx + 1}</td>
                            <td>
                              <button
                                className="btn btn-link p-0"
                                style={{ color: '#810055', fontSize: '0.82rem' }}
                                onClick={(e) => { e.stopPropagation(); setDetailRow(row); }}
                              >
                                {row.invoiceNo}
                              </button>
                            </td>
                            <td>{row.company}</td>
                            <td>{row.transactionType}</td>
                            <td>{row.division}</td>
                            <td>{row.transporterCode}</td>
                            <td>{row.loadNumber}</td>
                            <td>{row.invoiceDate}</td>
                            <td>{row.iodDate || '—'}</td>
                            <td>{row.iodTime || '—'}</td>
                            <td><StatusBadge status={row.status} /></td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === TAB_UPLOAD && (
          <div className={styles['iod-form-panel']}>
            <div className={styles['panel-title']}>Upload IOD via Excel</div>
            <p style={{ fontSize: '0.82rem', color: '#6c757d' }}>
              Excel columns required: <strong>Company, Transaction Type, Invoice No, IOD Date, IOD Time</strong>
            </p>
            <Row className="g-2 align-items-end">
              <Col md={4}>
                <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
              </Col>
              <Col md="auto">
                <button className={`btn ${styles['btn-iod-primary']}`} onClick={handleUpload} disabled={uploading || !uploadFile}>
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              </Col>
            </Row>

            {uploadResult && (
              <div className={`${styles['upload-result']} ${uploadResult.failed > 0 ? styles.partial : styles.success}`}>
                <strong>Upload complete</strong> — {uploadResult.processed} processed, {uploadResult.failed} failed
                {uploadResult.errors?.length > 0 && (
                  <div className={styles['iod-table-wrap']} style={{ marginTop: 10 }}>
                    <table className={`table ${styles['iod-table']}`}>
                      <thead>
                        <tr>
                          <th>Invoice No</th>
                          <th>Company</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.errors.map((e, i) => (
                          <tr key={i}>
                            <td>{e.invoiceNo}</td>
                            <td>{e.company}</td>
                            <td style={{ color: '#dc3545' }}>{e.errorMsg}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Container>

      {/* Detail modal */}
      <Modal show={!!detailRow} onHide={() => setDetailRow(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Invoice Detail — {detailRow?.invoiceNo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailRow && (
            <Row>
              {[
                ['Invoice No', detailRow.invoiceNo],
                ['Company', detailRow.company],
                ['Transaction Type', detailRow.transactionType],
                ['Division', detailRow.division],
                ['Invoice Date', detailRow.invoiceDate],
                ['Transporter Code', detailRow.transporterCode],
                ['Load Number', detailRow.loadNumber],
                ['IOD Date', detailRow.iodDate || '—'],
                ['IOD Time', detailRow.iodTime || '—'],
                ['Status', detailRow.status],
              ].map(([label, val]) => (
                <Col md={6} key={label} className="mb-2">
                  <small className="text-muted d-block">{label}</small>
                  <strong>{val}</strong>
                </Col>
              ))}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailRow(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IodUpdate;
